import React, { useState } from 'react';
import { get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Check } from '@mui/icons-material';
import { useQueryClient } from 'react-query';
import { useModal } from 'mui-modal-provider';
import { useNavigate } from 'react-router-dom';

import Attendances from '../../../../Utils/Types/Attendances';
import ButtonWithLoader from '../../../ButtonWithLoader';
import { loginPath } from '../../../../Utils/routes';
import DownloadICSButton from './DownloadICSButton/DownloadICSButton';
import { Post } from '../../../../Utils/Types/Posts';
import queriesConfig from '../../../../Services/queriesConfig';
import useApiQuery from '../../../../Hooks/useApiQuery';
import useApiMutation from '../../../../Hooks/useApiMutation';
import EntitySubscriptionModal from '../../../EntitySubscriptionModal';
import useSpace from '../../../../Hooks/useSpace';
import useCurrentUser from '../../../../Hooks/useCurrentUser';

interface CommunicationActionsProps {
  post: Post;
}

function CommunicationActions({ post }: CommunicationActionsProps) {
  const queryClient = useQueryClient();
  const [attendance, setAttendance] = useState<any>();
  const { space } = useSpace();
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { isLoggedIn } = useCurrentUser();
  const attending = get(attendance, 'attendance_status') === Attendances.ATTENDING;

  const { isLoading: isLoadingFetch } = useApiQuery({
    query: queriesConfig.getAttendances(post.id),
    onSuccess: (data: any) => {
      setAttendance(data[0]);
    },
  });

  const { mutate: createAttendance, isLoading: isLoadingCreate } = useApiMutation({
    query: queriesConfig.createAttendance(post.id, Attendances.ATTENDING),
    onSuccess: (data: any) => {
      setAttendance(data[0]);
      queryClient.invalidateQueries(queriesConfig.getPost(post.id).queryKey);
      queryClient.invalidateQueries(queriesConfig.getAttendances(post.id).queryKey);
    },
  });

  const { mutate: updateAttendance, isLoading: isLoadingUpdate } = useApiMutation({
    query: queriesConfig.updateAttendance(attendance?.id),
    onSuccess: (data: any) => {
      setAttendance(data);
      queryClient.invalidateQueries(queriesConfig.getPost(post.id).queryKey);
    },
  });

  const isLoading = isLoadingFetch || isLoadingCreate || isLoadingUpdate;

  const create = async () => {
    if (!isLoggedIn) {
      navigate(loginPath());
    } else {
      if (!space.entity_subscription) {
        showModal(EntitySubscriptionModal);
      } else {
        createAttendance();
      }
    }
  };

  const changeAttendance = async (status: any) => {
    updateAttendance(status);
  };

  return (
    <>
      {attending ? (
        <ButtonWithLoader
          variant="contained"
          color="primary"
          size="large"
          disabled={isLoading}
          startIcon={<Check />}
          isFetching={isLoading}
          onClick={() => changeAttendance(Attendances.DECLINED)}
          fullWidth
        >
          <FormattedMessage id="ATTENDANCE.ATTENDING" />
        </ButtonWithLoader>
      ) : (
        <ButtonWithLoader
          variant="outlined"
          color="primary"
          size="large"
          disabled={isLoading}
          isFetching={isLoading}
          onClick={() => (attendance ? changeAttendance(Attendances.ATTENDING) : create())}
          fullWidth
        >
          <FormattedMessage id="ATTENDANCE.ATTEND" />
        </ButtonWithLoader>
      )}

      <DownloadICSButton post={post} />
    </>
  );
}

export default CommunicationActions;
